import { useState } from 'react';
import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { TraskCreateDialog } from './task-create-dialog.tsx';

export type DeleteTaskDialogProps = {
    taskId?: string;
    onDeleted?: () => void;
} & ModalInnerProps;

export const DeleteTaskDialog = DialogStateManager.create<DeleteTaskDialogProps>(
    ({ taskId, onDeleted }: DeleteTaskDialogProps) => {
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const [isDeletingTask, setIsDeletingTask] = useState(false);
        const modal = useDialog();
        const apiUtils = api.useUtils();

        const deleteMutation = api.platoUser.command.tasks.postTasksActions.delete.useMutation({
            onSuccess() {
                console.debug('Task deleted', taskId);

                apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();
                apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate();
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate();
                apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate();

                toast({
                    icon: 'success',
                    title: t('tasks__toast__deleted_success_title'),
                    deletedLink: (
                        <LinkText
                            onClick={(e) => {
                                e.preventDefault();
                                void DialogStateManager.show(TraskCreateDialog, {
                                    source: 'task_deleted_toast',
                                });
                                return null;
                            }}
                        />
                    ),
                });

                onDeleted?.();

                modal.remove();
            },
        });

        const handleSubmit = async () => {
            if (taskId) {
                setIsDeletingTask(true);

                deleteMutation.mutate({
                    taskId,
                });
            }
        };

        const handleCloseConfirmation = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('tasks__delete_confirmation__title')}
                description={t('tasks__delete_confirmation__body')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCloseConfirmation}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeletingTask}>
                        {t('form__delete', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
